/**
 * Bible scriptures organized by context for loading animations
 * Each scripture is relevant to specific ministry functions
 */

export interface Scripture {
  text: string;
  reference: string;
  context: ScriptureContext[];
}

export type ScriptureContext =
  | 'ministry'
  | 'giving'
  | 'engagement'
  | 'planning'
  | 'analytics'
  | 'learning'
  | 'attendance'
  | 'general';

export const scriptures: Scripture[] = [
  // Ministry & Leadership
  {
    text: "Be shepherds of God's flock that is under your care, watching over them—not because you must, but because you are willing, as God wants you to be.",
    reference: "1 Peter 5:2",
    context: ['ministry', 'general']
  },
  {
    text: "Keep watch over yourselves and all the flock of which the Holy Spirit has made you overseers. Be shepherds of the church of God, which he bought with his own blood.",
    reference: "Acts 20:28",
    context: ['ministry', 'general']
  },
  {
    text: "Not so with you. Instead, whoever wants to become great among you must be your servant.",
    reference: "Matthew 20:26",
    context: ['ministry']
  },

  // Giving & Stewardship
  {
    text: "Remember this: Whoever sows sparingly will also reap sparingly, and whoever sows generously will also reap generously.",
    reference: "2 Corinthians 9:6",
    context: ['giving']
  },
  {
    text: "Each of you should give what you have decided in your heart to give, not reluctantly or under compulsion, for God loves a cheerful giver.",
    reference: "2 Corinthians 9:7",
    context: ['giving']
  },
  {
    text: "Bring the whole tithe into the storehouse, that there may be food in my house. Test me in this, says the Lord Almighty, and see if I will not throw open the floodgates of heaven.",
    reference: "Malachi 3:10",
    context: ['giving']
  },
  {
    text: "Honor the Lord with your wealth, with the firstfruits of all your crops.",
    reference: "Proverbs 3:9",
    context: ['giving']
  },

  // Engagement & Fellowship
  {
    text: "And let us consider how we may spur one another on toward love and good deeds, not giving up meeting together, as some are in the habit of doing.",
    reference: "Hebrews 10:24-25",
    context: ['engagement', 'attendance']
  },
  {
    text: "They devoted themselves to the apostles' teaching and to fellowship, to the breaking of bread and to prayer.",
    reference: "Acts 2:42",
    context: ['engagement', 'attendance']
  },
  {
    text: "Therefore encourage one another and build each other up, just as in fact you are doing.",
    reference: "1 Thessalonians 5:11",
    context: ['engagement']
  },
  {
    text: "A new command I give you: Love one another. As I have loved you, so you must love one another.",
    reference: "John 13:34",
    context: ['engagement', 'general']
  },

  // Planning & Strategy
  {
    text: "Commit to the Lord whatever you do, and he will establish your plans.",
    reference: "Proverbs 16:3",
    context: ['planning']
  },
  {
    text: "For I know the plans I have for you, declares the Lord, plans to prosper you and not to harm you, plans to give you hope and a future.",
    reference: "Jeremiah 29:11",
    context: ['planning', 'general']
  },
  {
    text: "Plans fail for lack of counsel, but with many advisers they succeed.",
    reference: "Proverbs 15:22",
    context: ['planning']
  },
  {
    text: "In their hearts humans plan their course, but the Lord establishes their steps.",
    reference: "Proverbs 16:9",
    context: ['planning']
  },

  // Analytics & Assessment
  {
    text: "Suppose one of you wants to build a tower. Won't you first sit down and estimate the cost to see if you have enough money to complete it?",
    reference: "Luke 14:28",
    context: ['analytics', 'planning']
  },
  {
    text: "The plans of the diligent lead to profit as surely as haste leads to poverty.",
    reference: "Proverbs 21:5",
    context: ['analytics', 'planning']
  },

  // Learning & Growth
  {
    text: "Let the wise listen and add to their learning, and let the discerning get guidance.",
    reference: "Proverbs 1:5",
    context: ['learning']
  },
  {
    text: "Instruct the wise and they will be wiser still; teach the righteous and they will add to their learning.",
    reference: "Proverbs 9:9",
    context: ['learning']
  },
  {
    text: "Do your best to present yourself to God as one approved, a worker who does not need to be ashamed and who correctly handles the word of truth.",
    reference: "2 Timothy 2:15",
    context: ['learning']
  },

  // General Encouragement
  {
    text: "I can do all this through him who gives me strength.",
    reference: "Philippians 4:13",
    context: ['general']
  },
  {
    text: "Trust in the Lord with all your heart and lean not on your own understanding; in all your ways submit to him, and he will make your paths straight.",
    reference: "Proverbs 3:5-6",
    context: ['general']
  },
  {
    text: "For God has not given us a spirit of fear, but of power and of love and of a sound mind.",
    reference: "2 Timothy 1:7",
    context: ['general']
  },
  {
    text: "Be strong and courageous. Do not be afraid; do not be discouraged, for the Lord your God will be with you wherever you go.",
    reference: "Joshua 1:9",
    context: ['general']
  }
];

/**
 * Get a random scripture for a specific context
 */
export const getScriptureForContext = (context: ScriptureContext): Scripture => {
  const contextScriptures = scriptures.filter(s => s.context.includes(context));

  if (contextScriptures.length === 0) {
    // Fallback to general if no match
    const generalScriptures = scriptures.filter(s => s.context.includes('general'));
    return generalScriptures[Math.floor(Math.random() * generalScriptures.length)];
  }

  return contextScriptures[Math.floor(Math.random() * contextScriptures.length)];
};

/**
 * Get multiple scriptures for a context (for longer loading times)
 */
export const getScripturesForContext = (context: ScriptureContext, count: number = 3): Scripture[] => {
  const contextScriptures = scriptures.filter(s => s.context.includes(context));

  if (contextScriptures.length === 0) {
    const generalScriptures = scriptures.filter(s => s.context.includes('general'));
    return shuffleArray(generalScriptures).slice(0, count);
  }

  return shuffleArray(contextScriptures).slice(0, count);
};

/**
 * Shuffle array helper
 */
const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};
